import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Flag, CheckCircle, TrendingUp, Eye, Clock } from 'lucide-react';
import { TimelinePoint } from '../hooks/types/metricsTypes';
import { PitchTimePicker } from '../retention/PitchTimePicker';
import type { VideoPitch } from '@/types/pitch';
import {
  parseTimeToSeconds,
  formatSecondsToTime,
  calculateRetentionAtTime,
  generateHistogramData,
  viewersAtTime,
} from './utils';
import {
  formatPitchTime,
  knownDuration,
  pitchLabel,
  pitchSaveErrorMessage,
  upsertPitch,
  validatePitches,
} from '@/utils/pitches';
import { metricsService } from '@/services/metricsService';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { toast as sonnerToast } from 'sonner';
import {
  Area,
  AreaChart,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
} from 'recharts';

interface PitchConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Vídeo dono dos pitches — é nele que o PUT grava. */
  videoId: string | null;
  /** Lista COMPLETA do vídeo: o PUT substitui a lista inteira. */
  pitches: VideoPitch[];
  /** Pitch aberto em "Detalhes". */
  pitchId: string | null;
  /** Duração do vídeo em segundos (teto do tempo do pitch). */
  durationInSeconds?: number | null;
  timelineData: TimelinePoint[];
  /** Recebe a lista salva; o pai refaz o fetch das métricas. */
  onSaved?: (pitches: VideoPitch[]) => void | Promise<void>;
}

export function PitchConfigModal({
  isOpen,
  onClose,
  videoId,
  pitches,
  pitchId,
  durationInSeconds,
  timelineData,
  onSaved,
}: PitchConfigModalProps) {
  useLanguage();
  const [pitchTime, setPitchTime] = useState('00:00');
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { toast } = useToast();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('videos', 'edit');

  const pitchIndex = pitchId ? pitches.findIndex((p) => p.id === pitchId) : -1;
  const pitch = pitchIndex >= 0 ? pitches[pitchIndex] : null;
  const label = pitch ? pitchLabel(pitch, pitchIndex) : 'Pitch';
  const savedSeconds = pitch?.seconds ?? null;

  useEffect(() => {
    if (isOpen && !canEdit) {
      sonnerToast.error('Sem permissão para editar pitches');
      onClose();
    }
  }, [isOpen, canEdit, onClose]);

  // Initialize with the open pitch time
  useEffect(() => {
    setPitchTime(savedSeconds != null ? formatPitchTime(savedSeconds) : '00:00');
  }, [savedSeconds, isOpen]);

  // Calculate retention at pitch (interpolation)
  const retentionAtPitch = useMemo(() => {
    if (pitchTime === '00:00' || !timelineData || timelineData.length === 0) return 0;
    const seconds = parseTimeToSeconds(pitchTime);
    const retention = calculateRetentionAtTime(seconds, timelineData);
    // Garantir que está entre 0 e 100
    return Math.max(0, Math.min(100, retention));
  }, [pitchTime, timelineData]);

  // Get viewers count at pitch from timeline data
  const viewsAtPitch = useMemo(() => {
    if (pitchTime === '00:00' || !timelineData || timelineData.length === 0) return 0;
    return viewersAtTime(parseTimeToSeconds(pitchTime), timelineData);
  }, [pitchTime, timelineData]);

  // Generate histogram data (de 5 em 5 segundos, igual à retenção)
  const histogramData = useMemo(() => {
    if (pitchTime === '00:00' || !timelineData || timelineData.length === 0) return [];
    const seconds = parseTimeToSeconds(pitchTime);

    // Validar que o pitch está dentro do range do vídeo
    const maxTime = Math.max(...timelineData.map(p => p.time));
    if (seconds > maxTime) return [];

    // Gerar dados de 5 em 5 segundos ao redor do pitch
    return generateHistogramData(seconds, timelineData, 50, 5);
  }, [pitchTime, timelineData]);

  // Get pitch time in seconds for reference line
  const pitchSeconds = useMemo(() => parseTimeToSeconds(pitchTime), [pitchTime]);

  // Salva o novo tempo do pitch aberto, mandando a lista inteira para o PUT
  const handleSave = async () => {
    if (pitchTime === '00:00' || !pitch) return;

    const list = upsertPitch(pitches, pitch.id, {
      name: pitch.name,
      seconds: parseTimeToSeconds(pitchTime),
    });
    const validationError = validatePitches(list, durationInSeconds);
    if (validationError) {
      toast({ title: translateInterfaceText("Erro"), description: validationError, variant: 'destructive' });
      return;
    }
    if (!videoId) {
      toast({ title: translateInterfaceText("Erro"), description: translateInterfaceText("Vídeo não encontrado para salvar o pitch."), variant: 'destructive' });
      return;
    }

    setIsSaving(true);
    try {
      const saved = await metricsService.updatePitches(videoId, list);
      await onSaved?.(saved);

      toast({
        title: translateInterfaceText("Pitch atualizado"),
        description: translateInterfaceText("O tempo do pitch foi salvo com sucesso."),
      });

      // Fechar modal imediatamente
      onClose();
    } catch (error) {
      console.error('Error saving pitch:', error);
      toast({
        title: translateInterfaceText("Erro ao salvar"),
        description: pitchSaveErrorMessage(error),
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle Enter key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && pitchTime !== '00:00') {
      handleSave();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="sm:max-w-[560px] max-h-[85vh] rounded-[18px] border-0 p-8 pb-10 flex flex-col mt-[1%]"
        onKeyDown={handleKeyDown}
      >
        <DialogHeader className="pb-2 flex-shrink-0">
          <DialogTitle className="text-[18px] font-semibold flex items-center gap-2.5 text-[#1C2434]">
            <Flag className="h-5 w-5 text-[#4B7BFF]" />
            {translateInterfaceText("Configurar")}{" "}{label}
          </DialogTitle>
          <DialogDescription className="text-[14px] font-normal text-[#6E7483] mt-1">
            {translateInterfaceText("Defina o momento exato onde sua oferta principal aparece no vídeo")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-4 pr-4 overflow-y-auto flex-1">
          {/* Input do tempo - Premium */}
          <div>
            <label className="text-[14px] font-medium text-[#2C3345] mb-2.5 flex items-center gap-2">
              <Clock className="h-4 w-4 text-[#5A6475]" />
              {translateInterfaceText("Tempo do pitch:")}</label>
            <PitchTimePicker
              value={pitchTime}
              onChange={setPitchTime}
              onClear={() => setPitchTime('00:00')}
              videoLength={knownDuration(durationInSeconds) ?? undefined}
            />
          </div>

          {/* Cards de Performance - Premium 10/10 */}
          {pitchTime !== '00:00' && retentionAtPitch > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Card 1 - Retenção no Pitch */}
              <div
                className="bg-white border border-[#EDF1FF] rounded-[16px] p-5 space-y-2"
                style={{ boxShadow: '0px 10px 24px rgba(0, 0, 0, 0.04)' }}
              >
                <div className="flex items-center gap-2 text-[#5A6475]">
                  <TrendingUp className="h-4 w-4" />
                  <p className="text-[14px] font-medium">{translateInterfaceText("Retenção no pitch")}</p>
                </div>
                <div className="flex items-baseline gap-2.5">
                  <p className="text-[26px] font-bold text-[#1C2434]">
                    {Math.round(retentionAtPitch * 10) / 10}%
                  </p>
                  <Badge
                    className={`text-[11px] font-semibold px-2.5 py-1 rounded-lg ${
                      retentionAtPitch >= 60
                        ? 'bg-[#EDF4FF] text-[#1D4FFF]'
                        : retentionAtPitch >= 40
                          ? 'bg-[#FFF8E1] text-[#F57F17]'
                          : 'bg-[#FFF2F0] text-[#CE3D3D]'
                    }`}
                  >
                    {retentionAtPitch >= 60 ? translateInterfaceText("Excelente") : retentionAtPitch >= 40 ? translateInterfaceText("Regular") : translateInterfaceText("Atenção")}
                  </Badge>
                </div>
                <p className="text-[12px] font-normal text-[#8992A3]">
                  {translateInterfaceText("Porcentagem de pessoas que ainda estavam assistindo")}</p>
              </div>

              {/* Card 2 - Visualizações */}
              <div
                className="bg-white border border-[#EDF1FF] rounded-[16px] p-5 space-y-2"
                style={{ boxShadow: '0px 10px 24px rgba(0, 0, 0, 0.04)' }}
              >
                <div className="flex items-center gap-2 text-[#5A6475]">
                  <Eye className="h-4 w-4" />
                  <p className="text-[14px] font-medium">{translateInterfaceText("Visualizações")}</p>
                </div>
                <p className="text-[26px] font-bold text-[#1C2434]">
                  {viewsAtPitch.toLocaleString(getInterfaceLanguage())}
                </p>
                <p className="text-[12px] font-normal text-[#8992A3]">
                  {translateInterfaceText("Total de visualizações do vídeo")}</p>
              </div>
            </div>
          )}

          {/* Gráfico de Retenção Premium 10/10 */}
          {pitchTime !== '00:00' && histogramData.length > 0 && (
            <div>
              <p className="text-[14px] font-medium text-[#2C3345] mb-3">
                {translateInterfaceText("Retenção próximo ao pitch")}</p>
              
              <ResponsiveContainer width="100%" height={160}>
                <AreaChart 
                  data={histogramData}
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="colorRetentionPitch" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#4B7BFF" stopOpacity={0.18} />
                      <stop offset="100%" stopColor="#4B7BFF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  
                  <CartesianGrid strokeDasharray="3 3" stroke="#EDF1FF" opacity={0.8} />
                  
                  <XAxis
                    dataKey="time"
                    tickFormatter={(v) => formatSecondsToTime(v)}
                    tick={{ fontSize: 11, fill: '#8992A3' }}
                    stroke="#EDF1FF"
                    axisLine={{ strokeWidth: 1 }}
                  />
                  
                  <YAxis
                    domain={[0, 100]}
                    ticks={[0, 25, 50, 75, 100]}
                    tick={{ fontSize: 11, fill: '#8992A3' }}
                    stroke="#EDF1FF"
                    axisLine={{ strokeWidth: 1 }}
                    tickFormatter={(value) => `${value}%`}
                  />
                  
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload || !payload.length) return null;
                      const data = payload[0].payload;
                      return (
                        <div
                          className="bg-white rounded-[14px] min-w-[180px]"
                          style={{
                            border: '1px solid #E4EBFF',
                            boxShadow: '0px 14px 28px rgba(0, 0, 0, 0.03)',
                            padding: '16px 20px',
                          }}
                        >
                          <p className="text-[14px] font-semibold text-[#1C2434]">
                            {translateInterfaceText("TEMPO:")}{" "}{formatSecondsToTime(data.time)}
                          </p>
                          <div
                            style={{
                              height: '1px',
                              background: '#EDF1FF',
                              margin: '8px 0 10px 0'
                            }}
                          />
                          <div className="space-y-2">
                            <div className="flex items-center justify-between gap-6">
                              <span className="text-[13px] text-[#5A6475] font-medium">{translateInterfaceText("Retenção")}</span>
                              <span className="text-[14px] font-semibold text-[#2B3343]">
                                {Math.round(data.retention * 10) / 10}%
                              </span>
                            </div>
                            <div className="flex items-center justify-between gap-6">
                              <span className="text-[13px] text-[#5A6475] font-medium">{translateInterfaceText("Visualizações")}</span>
                              <span className="text-[14px] font-semibold text-[#2B3343]">
                                {viewersAtTime(data.time, timelineData).toLocaleString(getInterfaceLanguage())}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />

                  <Area
                    type="monotone"
                    dataKey="retention"
                    stroke="#4B7BFF"
                    strokeWidth={2.4}
                    fill="url(#colorRetentionPitch)"
                    style={{ filter: 'drop-shadow(0px 0px 6px rgba(75, 123, 255, 0.3))' }}
                    animationDuration={800}
                  />

                  {/* Marcação do pitch aberto - igual à aba de retenção */}
                  <ReferenceLine
                    x={pitchSeconds}
                    stroke="#F59E0B"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    label={{
                      value: label,
                      position: 'top',
                      fill: "#F59E0B",
                      fontSize: 13,
                      fontWeight: 600,
                      offset: 10
                    }}
                  />
                </AreaChart>
              </ResponsiveContainer>
              
              <div className="flex items-center justify-center gap-5 mt-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#4B7BFF]"></div>
                  <p className="text-[12px] text-[#8992A3] font-medium">{translateInterfaceText("Retenção")}</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#F59E0B]"></div>
                  <p className="text-[12px] text-[#8992A3] font-medium">{translateInterfaceText("Pitch")}</p>
                </div>
              </div>
            </div>
          )}

          {/* Observação - Hierarquia Premium */}
          {pitchTime !== '00:00' && retentionAtPitch > 0 && (
            <div className="space-y-1">
              <p className="text-[14px] font-semibold text-[#2C3345]">
                {translateInterfaceText("Observação:")}</p>
              <p className="text-[13px] font-medium text-[#737B8A] leading-relaxed">
                {translateInterfaceText("Seu pitch ocorre quando")}{' '}
                <span className="font-semibold text-[#4B7BFF]">{Math.round(retentionAtPitch * 10) / 10}%</span>{' '}
                {translateInterfaceText("da audiência ainda está assistindo (")}<span className="font-semibold text-[#2C3345]">{viewsAtPitch.toLocaleString(getInterfaceLanguage())}</span>{' '}
                {translateInterfaceText("visualizações).")}</p>
            </div>
          )}

          {/* Toast inline de sucesso */}
          {showSuccess && (
            <div className="bg-[#E8F5E9] border border-[#A5D6A7] rounded-[10px] p-3.5 flex items-center gap-2.5">
              <CheckCircle className="h-5 w-5 text-[#2E7D32]" />
              <p className="text-[14px] font-medium text-[#1B5E20]">
                {translateInterfaceText("Pitch atualizado com sucesso!")}</p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-3 pt-4 border-t border-[#EDF1FF] mt-2 flex-shrink-0">
          {!showSuccess ? (
            <>
              <Button 
                variant="outline" 
                onClick={onClose} 
                className="border-[#CBD4E4] text-[#475067] bg-transparent hover:bg-[#F5F7FB] hover:text-[#475067] rounded-[10px] font-medium"
              >
                {translateInterfaceText("Cancelar")}</Button>
              <Button
                onClick={handleSave}
                disabled={isSaving || pitchTime === '00:00'}
                className="bg-[#4B7BFF] text-white font-semibold rounded-[10px] hover:bg-[#3F6AE3]"
                style={{ boxShadow: '0px 6px 12px rgba(75, 123, 255, 0.25)' }}
              >
                {isSaving ? translateInterfaceText("Salvando...") : translateInterfaceText("Salvar")}
              </Button>
            </>
          ) : (
            <Button 
              onClick={onClose}
              className="bg-[#4B7BFF] text-white font-semibold rounded-[10px] hover:bg-[#3F6AE3]"
              style={{ boxShadow: '0px 6px 12px rgba(75, 123, 255, 0.25)' }}
            >
              {translateInterfaceText("Fechar")}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
